import { ReadonlyMat4, ReadonlyVec2, mat4, quat, vec3 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';
import { RegionOfInterest } from '../../../data/structure/RegionOfInterest';
import { View } from '../../../view/View';
import { ViewManager } from '../../../view/ViewManager';
import { VolumeViewUpdateEvent } from '../../../view/events/VolumeViewUpdateEvent';
import { PlaneView } from '../../../view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import {
    BrushDimensions,
    BrushDrawAction,
    BrushDrawActionState,
} from './BrushDrawAction';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { VertexArrayObject } from '~/engine/core/utils/VertexArrayObject';
import { Edge } from '~/engine/data/common/Edge';
import { Vertex } from '~/engine/data/common/slicemesh/Vertex';
import { SliceMesh } from '~/engine/data/common/slicemesh/SliceMesh';
import { UniformInt } from '~/engine/core/utils/uniforms/UniformInt';

/**
 * {@link StaticBrushDrawActionState} is a {@link BrushDrawActionState} that stores the state of an active {@link StaticBrushDrawAction}.\
 * In addition to the {@link BrushDrawActionState}, it stores a {@link FrameBufferObject} and a {@link BrushDrawShader}.\
 * It uses the {@link FrameBufferObject} to draw data on the {@link drawObject}, which holds the user drawn data.\
 * It uses the {@link BrushDrawShader} to draw the data on the {@link FrameBufferObject}.
 */
class StaticBrushDrawActionState extends BrushDrawActionState {
    /**
     * {@link BrushDrawShader} that is used to render circles on the {@link drawObject}.\
     * So basically used to perform the drawing.
     */
    private readonly shader: BrushDrawShader;

    /**
     * {@link FrameBufferObject} that is used to draw the data on the {@link drawObject}.\
     * This {@link FrameBufferObject} does not hold any render targets within.\
     * It is used via the {@link FrameBufferObject.createTextureLayer} method to draw the data on the {@link drawObject}.
     */
    private readonly fbo: FrameBufferObject;

    /**
     * Constructor of {@link StaticBrushDrawActionState}.\
     * It initializes itself by calling the {@link BrushDrawActionState super} {@link BrushDrawActionState.constructor constructor},\
     * It then {@link FrameBufferObject.constructor initializes} the {@link fbo}, with {@link FrameBufferObject.size size} equal to that of the {@link drawObject}'s planarPixelSize.\
     * And the {@link BrushDrawShader}.\
     * It then calls the {@link StaticBrushDrawActionState.onMouseMove} method to draw the initial data on the {@link drawObject}.
     * @param view {@link PlaneView} on which the {@link StaticBrushDrawAction} is performed on.
     * @param targets {@link Array} of {@link RegionOfInterest} targets that this {@link StaticBrushDrawAction} modifies.
     * @param targetSpace {@link SliceSource} that defines the {@link SliceData slices} on which the drawing occurs on.
     * @param dimensions 2D or 3D brush.\
     * Note that even if dimensions are 2, the {@link drawObject} is still 3D, but the thickness is equal to thickness of a single slice + margins.
     * @param coordinates {@link ReadonlyVec2 Client pixel coordinates} of the mouse in between [0, pixelSize - 1] at the start of the {@link StaticBrushDrawAction}.
     * @param brushSize Diameter of the brush in millimeters.
     * This is also the distance at which values in the {@link drawObject} sdf turn from 0.5 to either 0 or 1.
     */
    public constructor(
        view: PlaneView,
        target: RegionOfInterest,
        targetImage: ImageObject,
        dimensions: BrushDimensions,
        coordinates: ReadonlyVec2,
        brushSize: number,
        erase: boolean
    ) {
        super(view, target, targetImage, dimensions, brushSize, erase);
        this.fbo = new FrameBufferObject(this.drawObject.planarPixelSize);
        this.shader = new BrushDrawShader();
        this.onMouseMove(coordinates, 0, view);
    }

    public get actionName(): string {
        return `Static brush ${this._erase ? 'erase' : 'draw'}`;
    }

    /**
     * Method to inform the {@link StaticBrushDrawActionState} about a mouse move event.\
     * If the {@link view} is not the same as the {@link StaticBrushDrawActionState.view}, {@link EventHandlerResult} of type {@link EventHandlerResultType.FINISH_ACTION FINISH_ACTION} is returned.\
     * This draws a circle or sphere at the new cursor location on the {@link drawObject}.\
     * After drawing, it generates a new {@link VolumeViewUpdateEvent}, sends it to the {@link ViewManager} and returns {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_HANDLED EVENT_HANDLED}.
     *
     * @param coordinates {@link ReadonlyVec2 Client pixel coordinates} of the mouse in between [0, pixelSize - 1] at the time of the event.
     * @param view {@link View} on which the event occurred.
     * @returns An {@link EventHandlerResult} with type {@link EventHandlerResultType.EVENT_HANDLED EVENT_HANDLED} if the {@link view} equals {@link StaticBrushDrawActionState.view this.view}
     * or {@link EventHandlerResultType.FINISH_ACTION FINISH_ACTION} if not.
     */
    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        view: View
    ): EventHandlerResult {
        if (view !== this._planeView) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        const patientCoordinates =
            this._planeView.clientToPatientCoordinates(coordinates);

        const mousePositionDrawObjectCoords = vec3.transformMat3(
            vec3.create(),
            patientCoordinates,
            this.drawObject.boundingBox.rotation
        );

        // Calculate a transformation for the BrushDrawShader to draw to the DrawObject texture
        const transformation = mat4.fromRotationTranslation(
            mat4.create(),
            quat.fromMat3(quat.create(), this.drawObject.boundingBox.rotation),
            mousePositionDrawObjectCoords
        );
        const sliceTransformation = mat4.create();
        const toProjection = mat4.create();
        const sliceTranslation = vec3.create();
        const scale = vec3.scale(
            vec3.create(),
            this.drawObject.boundingBox.size,
            0.5
        );
        // TODO: common function
        const drawObjectCenter = vec3.clone(this.drawObject.boundingBox.center);

        this.fbo.bind();
        gl.viewport(0, 0, this.fbo.width, this.fbo.height);
        gl.enable(gl.BLEND);
        gl.blendEquation(gl.MAX);
        gl.blendFunc(gl.ONE, gl.ONE);
        this.shader.start();
        const total = 2 * this.margin + this.brushSize;
        this.shader.radius.loadFloat(this.brushSize / total);
        this.shader.margin.loadFloat((2 * this.margin) / total);
        this.shader.dimensions.loadInt(this.dimensions as number);
        let i = 0;
        for (let z = 0; z < this.drawObject.grid.sizeZ; ++z) {
            this.fbo.createTextureLayer(
                this.drawObject.signedDistanceField.texture,
                i
            );

            drawObjectCenter[2] = this.drawObject.grid.getNormalPosition(z);
            vec3.transformMat3(
                sliceTranslation,
                drawObjectCenter,
                this.drawObject.boundingBox.rotation
            );

            mat4.fromRotationTranslationScale(
                sliceTransformation,
                quat.fromMat3(
                    quat.create(),
                    this.drawObject.boundingBox.rotation
                ),
                sliceTranslation,
                scale
            );
            mat4.invert(toProjection, sliceTransformation);
            const vao = this.edgeIntersectionsWithPlane(
                toProjection,
                transformation
            );
            if (vao) {
                vao.bind(0, 1);
                gl.drawElements(
                    gl.TRIANGLE_STRIP,
                    vao.indexCount,
                    gl.UNSIGNED_INT,
                    0
                );
                vao.unbind(0, 1);
                vao.delete();
            }
            ++i;
        }
        this.shader.stop();
        gl.blendEquation(gl.FUNC_ADD);
        gl.disable(gl.BLEND);
        this.fbo.unbind();
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(this.drawObject, 0, false)
        );
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    private _brushEdges: Edge[] | undefined;

    private get brushEdges(): Edge[] {
        if (!this._brushEdges) {
            this._brushEdges = this.generateBrushMesh();
        }
        return this._brushEdges;
    }

    private generateBrushMesh(): Edge[] {
        const halfSize = this.brushSize * 0.5 + this.margin;
        const normalDirection = this.targetImage.grid.getUnitZ();
        const sizeZ =
            this.dimensions === BrushDimensions.Brush3D
                ? halfSize
                : vec3.dot(
                      vec3.fromValues(
                          this.resolution[0],
                          this.resolution[1],
                          this.targetImage.grid.resZ
                      ),
                      normalDirection
                  );

        const vertices = [
            new Vertex(
                vec3.fromValues(-halfSize, -halfSize, -sizeZ),
                vec3.fromValues(-1.0, -1.0, -1.0)
            ),
            new Vertex(
                vec3.fromValues(-halfSize, -halfSize, sizeZ),
                vec3.fromValues(-1.0, -1.0, 1.0)
            ),
            new Vertex(
                vec3.fromValues(-halfSize, halfSize, -sizeZ),
                vec3.fromValues(-1.0, 1.0, -1.0)
            ),
            new Vertex(
                vec3.fromValues(-halfSize, halfSize, sizeZ),
                vec3.fromValues(-1.0, 1.0, 1.0)
            ),
            new Vertex(
                vec3.fromValues(halfSize, -halfSize, -sizeZ),
                vec3.fromValues(1.0, -1.0, -1.0)
            ),
            new Vertex(
                vec3.fromValues(halfSize, -halfSize, sizeZ),
                vec3.fromValues(1.0, -1.0, 1.0)
            ),
            new Vertex(
                vec3.fromValues(halfSize, halfSize, -sizeZ),
                vec3.fromValues(1.0, 1.0, -1.0)
            ),
            new Vertex(
                vec3.fromValues(halfSize, halfSize, sizeZ),
                vec3.fromValues(1.0, 1.0, 1.0)
            ),
        ];
        return [
            new Edge(vertices[0], vertices[1]),
            new Edge(vertices[0], vertices[2]),
            new Edge(vertices[0], vertices[4]),
            new Edge(vertices[1], vertices[3]),
            new Edge(vertices[1], vertices[5]),
            new Edge(vertices[2], vertices[3]),
            new Edge(vertices[2], vertices[6]),
            new Edge(vertices[3], vertices[7]),
            new Edge(vertices[4], vertices[5]),
            new Edge(vertices[4], vertices[6]),
            new Edge(vertices[5], vertices[7]),
            new Edge(vertices[6], vertices[7]),
        ];
    }

    private edgeIntersectionsWithPlane(
        toProjection: ReadonlyMat4,
        transformation: ReadonlyMat4
    ): VertexArrayObject | undefined {
        const edges = this.brushEdges;

        const intersections = new Array<Vertex>();
        let intersection: Vertex | undefined;
        for (const edge of edges) {
            const transformedEdge = new Edge(
                new Vertex(edge.start).transform(transformation),
                new Vertex(edge.end).transform(transformation)
            );
            if (
                (intersection = transformedEdge.intersection(
                    toProjection,
                    edges.length === 4
                ))
            ) {
                intersections.push(intersection);
            }
        }
        if (intersections.length > 0) {
            const first = intersections[0];
            const buffers = [
                new Float32Array(intersections.length * 2),
                ...first.map(
                    (component) =>
                        new Float32Array(
                            intersections.length * component.dimensions
                        )
                ),
            ];
            let index = 0;
            for (const v of intersections) {
                buffers[0].set([v.coordinates[0], v.coordinates[1]], index * 2);
                let j = 1;
                for (const c of v.components) {
                    buffers[j++].set(c.data, index * c.dimensions);
                }
                ++index;
            }

            const indices = SliceMesh.tesselate(buffers[0], 0);
            if (indices) {
                const vao = new VertexArrayObject();
                vao.bind();
                vao.createAttribute(0, gl.FLOAT, buffers[0], 2);
                for (let i = 1; i < buffers.length; ++i) {
                    vao.createAttribute(
                        i,
                        gl.FLOAT,
                        buffers[i],
                        (buffers[i].length / intersections.length) as
                            | 1
                            | 2
                            | 3
                            | 4
                    );
                }
                vao.createIndexBuffer(indices);
                return vao;
            } else {
                throw new Error(
                    `Attempt to generate VAO for mesh failed due to lack of indices.`
                );
            }
        }
        return undefined;
    }

    /**
     * Method to clear the WebGL memory allocated by this {@link StaticBrushDrawActionState}.\
     * It calls the {@link BrushDrawActionState.clearMemory} method to clear the memory allocated by the {@link BrushDrawActionState}.\
     * It then deletes the {@link BrushDrawShader} and the {@link FrameBufferObject} and returns true.
     * @returns True to indicate that the WebGL memory was cleared.
     */
    public clearMemory() {
        super.clearMemory();
        this.shader.delete();
        this.fbo.delete();
    }
}

/**
 * {@link StaticBrushDrawAction} is a {@link BrushDrawAction} providing basic drawing functionality.
 */
export class StaticBrushDrawAction extends BrushDrawAction {
    /**
     * Constructor of {@link StaticBrushDrawAction}.\
     * It calls the {@link BrushDrawAction super} {@link BrushDrawAction.constructor constructor} with the {@link brushSize}, {@link drawButton}, {@link cancelButton} and {@link modifierKeys} as arguments.
     * @param brushSize Diameter of the brush in millimeters.
     * @param drawButton Mouse button that activates the {@link StaticBrushDrawAction}.
     * @param cancelButton Mouse button that cancels an active {@link StaticBrushDrawAction}.
     * @param modifierKeys {@link ModifierKeys} that need to be pressed in order to activate the {@link StaticBrushDrawAction}.
     */
    public constructor(
        private readonly target: RegionOfInterest,
        private readonly targetImage: ImageObject,
        brushSize: number,
        erase: boolean,
        drawButton: number,
        cancelButton: number,
        modifierKeys?: ModifierKeys
    ) {
        super(brushSize, erase, drawButton, cancelButton, modifierKeys);
    }

    /**
     * @override abstract {@link BrushDrawAction.startAction}
     * Method to start the {@link StaticBrushDrawAction}.\
     * It creates a new {@link StaticBrushDrawActionState} instance and returns it.
     * @param view The {@link PlaneView} on which the {@link StaticBrushDrawAction} is started.
     * @param targets {@link Array} of {@link RegionOfInterest} targets that this {@link StaticBrushDrawAction} modifies.
     * @param targetSpace {@link SliceSource} that defines the {@link SliceData slices} on which the drawing should reside on.
     * @param canvasCoordinates {@link ReadonlyVec2 Client pixel coordinates} of the mouse in between [0, pixelSize - 1] at the start of the {@link StaticBrushDrawAction}.
     * @returns A new {@link StaticBrushDrawActionState} instance, which is the newly created {@link activeEventState state} of this {@link StaticBrushDrawAction}.
     */
    public startAction(
        view: PlaneView,
        canvasCoordinates: ReadonlyVec2
    ): StaticBrushDrawActionState {
        const state = new StaticBrushDrawActionState(
            view,
            this.target,
            this.targetImage,
            BrushDimensions.Brush2D,
            canvasCoordinates,
            this.brushSize,
            this._erase
        );
        this._state = state;
        return state;
    }
}

/**
 * {@link BrushDrawShader} is a {@link ShaderProgram} that is used to draw sdf circles on the {@link StaticBrushDrawActionState.drawObject}.
 */
class BrushDrawShader extends ShaderProgram {
    /**
     * Vertex shader source code of the {@link BrushDrawShader}.\
     * It needs the following vertex variables:
     * - position: vec2, position of the vertex relative to the drawObject's texture space. Values in between [-1, 1].
     * - distance: vec3, distance of the vertex from the center of the circle. A value in between [-1, 1].
     *
     * It has a single input variable 'dimensions', which is an integer, defining the number of dimensions of the brush.\
     *
     * It has a single output variable, being the 'distanceFromCenter', which is equal to 'distance'.
     */
    private static readonly VERTEX_CONTENT = `#version 300 es
    precision highp float;
    
    in vec2 position;
    in vec3 distance;

    uniform int dimensions;

    out vec3 distanceFromCenter;
    
    void main(){
        gl_Position = vec4(position, 0.0, 1.0);
        distanceFromCenter = distance;
        if (dimensions == 2) {
            distanceFromCenter.z = 0.0;
        }

    }`;

    /**
     * Fragment shader source code of the {@link BrushDrawShader}.\
     * It has a single input variable, being the distanceFromCenter, which should be a value in between [-1, 1].\
     * And 2 uniforms:
     * - radius: float, radius of the circle in texture space. A value in between [0, 1].
     * - margin: float, distance of empty space around the circle in texture space. A value in between [0, 1].
     * It outputs a single float color value, being the sdf value of the circle at the given position.
     * @example
     * If brush diameter is 10 mm and margin is 10 mm, the radius should be 5/15 and margin should be 10/15.
     */
    private static readonly FRAGMENT_CONTENT = `#version 300 es
    precision lowp float;

    in vec3 distanceFromCenter;

    out float outColor;

    uniform float radius;
    uniform float margin;

    void main(){
        float distance = length(distanceFromCenter);
        outColor = 0.5 - 0.5 * (distance - radius) / margin;
    }`;

    /**
     * Uniform variable of type float, which defines the radius of the circle in texture space.\
     * This should be a value in between [0, 1].\
     * This is the radius relative to whole texture size. So with 10 mm diameter and 10 mm margin, the radius should be 5/15.
     */
    public readonly radius: UniformFloat;

    /**
     * Uniform variable of type float, which defines the distance of empty space around the circle in texture space.\
     * This should be a value in between [0, 1].\
     * This is the margin relative to whole texture size. So with 10 mm diameter and 10 mm margin, the margin should be 10/15.
     */
    public readonly margin: UniformFloat;

    /**
     * The number of dimensions (2 or 3) of the brush.\
     */
    public readonly dimensions: UniformInt;

    /**
     * Creates a new {@link BrushDrawShader}.\
     * It calls the {@link ShaderProgram.constructor} with the {@link BrushDrawShader.VERTEX_CONTENT} and {@link BrushDrawShader.FRAGMENT_CONTENT} as arguments.\
     * It then creates the {@link BrushDrawShader.radius} and {@link BrushDrawShader.margin} uniforms and stores their locations.
     */
    public constructor() {
        super(
            BrushDrawShader.VERTEX_CONTENT,
            BrushDrawShader.FRAGMENT_CONTENT,
            'position',
            'distance'
        );
        this.radius = new UniformFloat('radius');
        this.margin = new UniformFloat('margin');
        this.dimensions = new UniformInt('dimensions');
        super.storeUniformLocations(this.radius, this.margin, this.dimensions);
    }
}
